import { useEffect, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";

export interface ResidentsFilterForm {
  location_id?: number;
  floor_id?: number | number[];
  status?: string;
  gender?: string;
  status_flag_id?: number;
  search?: string;
  admission_start_date_from?: string;
  admission_start_date_to?: string;
  admission_end_date_from?: string;
  admission_end_date_to?: string;
}

const STORAGE_KEY = "residents_filter_state";

export function useResidentsFilterStorage(
  onFiltersLoaded: (filters: ResidentsFilterForm) => void,
) {
  const [isLoading, setIsLoading] = useState(true);

  // Load filters from storage on mount
  useEffect(() => {
    loadFiltersFromStorage();
  }, []);

  const loadFiltersFromStorage = async () => {
    try {
      const stored = await AsyncStorage.getItem(STORAGE_KEY);
      if (stored) {
        const filters: ResidentsFilterForm = JSON.parse(stored);
        onFiltersLoaded(filters);
      }
    } catch (e) {
      console.error("Error loading resident filters:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const saveFiltersToStorage = async (filters: ResidentsFilterForm) => {
    try {
      // Remove undefined/empty values before saving
      const cleanedFilters = Object.entries(filters).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== "" && value !== null) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, any>,
      );

      if (Object.keys(cleanedFilters).length > 0) {
        await AsyncStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(cleanedFilters),
        );
      } else {
        // If no filters, remove the storage key
        await AsyncStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      console.error("Error saving resident filters:", e);
    }
  };

  const clearFilters = async () => {
    try {
      await AsyncStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error("Error clearing resident filters:", e);
    }
  };

  return {
    isLoading,
    saveFiltersToStorage,
    clearFilters,
  };
}
